import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

const createSafeStorage = () => {
    if (typeof window === 'undefined') {
        const memoryStore = new Map()
        return {
            getItem: (name) => memoryStore.has(name) ? memoryStore.get(name) : null,
            setItem: (name, value) => memoryStore.set(name, value),
            removeItem: (name) => memoryStore.delete(name)
        }
    }

    try {
        const storage = window.localStorage
        const testKey = '__zustand_test__'
        storage.setItem(testKey, testKey)
        storage.removeItem(testKey)
        return storage
    } catch (_error) {
        const memoryStore = new Map()
        return {
            getItem: (name) => memoryStore.has(name) ? memoryStore.get(name) : null,
            setItem: (name, value) => memoryStore.set(name, value),
            removeItem: (name) => memoryStore.delete(name)
        }
    }
}

export const useStorePads = create(
    persist(
        (set) => ({
            pads: {},
            soundsLoaded: false,
            setSoundsLoaded: (value) => set({ soundsLoaded: value }),
            volume: 1,
            setVolume: (value) => set({ volume: value }),
            highpassFrequency: 20,
            setHighpassFrequency: (value) => set({ highpassFrequency: value }),
            lowpassFrequency: 20000,
            setLowpassFrequency: (value) => set({ lowpassFrequency: value }),
            showSetList: false,
            toggleSetList: () => set((state) => ({ showSetList: !state.showSetList })),
            playback: null,
            setPlayback: (playback) => set({ playback }),
            stopPlayback: () => set({
                playback: null,
                isMetronomeRunning: false
            }),
            selectedMidiDeviceId: '',
            setSelectedMidiDeviceId: (id) => set({ selectedMidiDeviceId: id }),
            midiAssignments: {},
            setMidiAssignment: (targetId, assignment) =>
                set((state) => ({
                    midiAssignments: {
                        ...state.midiAssignments,
                        [targetId]: assignment
                    }
                })),
            removeMidiAssignment: (targetId) =>
                set((state) => {
                    const midiAssignments = { ...state.midiAssignments }
                    delete midiAssignments[targetId]
                    return { midiAssignments }
                }),
            currentLibrary: {
                name: "Default"
            },
            setCurrentLibrary: (library) => set({
                currentLibrary: typeof library === 'string'
                    ? { name: library }
                    : library
            }),
            metronomeBpm: 120,
            setMetronomeBpm: (value) => set({ metronomeBpm: value }),
            metronomeMeasures: 4,
            setMetronomeMeasures: (value) => set({ metronomeMeasures: value }),
            metronomePan: -1,
            setMetronomePan: (value) => set({ metronomePan: value }),
            isMetronomeRunning: false,
            setMetronomeRunning: (value) => set({ isMetronomeRunning: value }),
            activateSongSettings: (song) => set((state) => ({
                currentLibrary: { name: song.library },
                playback: {
                    source: 'setlist',
                    title: song.title,
                    library: song.library,
                    note: song.key
                },
                metronomeBpm: Number.isFinite(song.bpm) ? song.bpm : state.metronomeBpm,
                metronomeMeasures: Number.isFinite(song.measures) ? song.measures : state.metronomeMeasures,
                isMetronomeRunning: Boolean(song.metronomeEnabled)
            })),
            libraries: [
                {
                    name: "Default",
                    sources: [
                        {
                            note: 'C',
                            url: '/pads/default/C.mp3'
                        },
                        {
                            note: 'C#',
                            url: '/pads/default/C-flat.mp3'
                        },
                        {
                            note: 'D',
                            url: '/pads/default/D.mp3'
                        },
                        {
                            note: 'D#',
                            url: '/pads/default/D-flat.mp3'
                        },
                        {
                            note: 'E',
                            url: '/pads/default/E.mp3'
                        },
                        {
                            note: 'F',
                            url: '/pads/default/F.mp3'
                        },
                        {
                            note: 'F#',
                            url: '/pads/default/F-flat.mp3'
                        },
                        {
                            note: 'G',
                            url: '/pads/default/G.mp3'
                        },
                        {
                            note: 'G#',
                            url: '/pads/default/G-flat.mp3'
                        },
                        {
                            note: 'A',
                            url: '/pads/default/A.mp3'
                        },
                        {
                            note: 'A#',
                            url: '/pads/default/A-flat.mp3'
                        },
                        {
                            note: 'B',
                            url: '/pads/default/B.mp3'
                        }
                    ],
                    disabled: false
                },
                {
                    name: "Worship",
                    sources: [],
                    disabled: true
                },
                {
                    name: "Clean organ",
                    sources: [],
                    disabled: true
                },
                {
                    name: "Dark pad",
                    sources: [],
                    disabled: true
                }
            ]
        }),
        {
            name: "easypads-store",
            version: 5,
            migrate: (persistedState, version) => {
                if (!persistedState) return persistedState
                const {
                    pads,
                    player,
                    playback,
                    ...rest
                } = persistedState
                return {
                    ...rest,
                    playback: null,
                    isMetronomeRunning: false,
                    highpassFrequency: persistedState.highpassFrequency ?? 20,
                    lowpassFrequency: persistedState.lowpassFrequency ?? 20000,
                    metronomePan: persistedState.metronomePan ?? -1
                }
            },
            storage: createJSONStorage(() => createSafeStorage()),
            partialize: (state) => ({
                soundsLoaded: state.soundsLoaded,
                volume: state.volume,
                highpassFrequency: state.highpassFrequency,
                lowpassFrequency: state.lowpassFrequency,
                metronomePan: state.metronomePan,
                currentLibrary: state.currentLibrary,
                libraries: state.libraries,
                metronomeBpm: state.metronomeBpm,
                metronomeMeasures: state.metronomeMeasures,
                selectedMidiDeviceId: state.selectedMidiDeviceId,
                midiAssignments: state.midiAssignments
            })
        }
    )
)

export const useStoreSongs = create(() => ({
    setlist: [],
    songs: []
}))
